import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import { CHART_COLORS } from "../../lib/chartTheme";
import { nf } from "../../lib/format";

export default function TrafficChart({
  data,
  visible,
  stacked = true,
  height = 160,
  barSize = 14,
}) {
  const hasData =
    Array.isArray(data) &&
    data.some(
      (d) =>
        (visible?.cam41 && d.k41) ||
        (visible?.chel && d.chel) ||
        (visible?.pool && d.pool)
    );

  const axisStyle = { fontSize: 11, fill: "#8aa0bd" };

  // Determine which series is the top-most in the stack
  // (based on the render order below: 41 -> Cheltenham -> Pool)
  const seriesOrder = [
    { key: "k41", show: !!visible?.cam41 },
    { key: "chel", show: !!visible?.chel },
    { key: "pool", show: !!visible?.pool },
  ];
  const topKey = seriesOrder.filter((s) => s.show).slice(-1)[0]?.key;

  const radiusFor = (key) => {
    if (!stacked) return [6, 6, 0, 0];       // grouped: light rounding on all
    return key === topKey ? [6, 6, 0, 0] : 0; // stacked: only top-most rounded
  };

  if (!hasData) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#8091a7",
          fontSize: 12,
        }}
      >
        No data to chart.
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
          barCategoryGap={stacked ? "20%" : "15%"}
        >
          <CartesianGrid stroke={CHART_COLORS.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tick={axisStyle}
            tickMargin={6}
            interval="preserveStartEnd"
            minTickGap={8}
          />
          <YAxis width={52} tickFormatter={(v) => nf.format(v)} tick={axisStyle} />
          <Tooltip
            formatter={(v) => nf.format(v)}
            cursor={{ fill: "rgba(255,255,255,0.03)" }}
            contentStyle={{
              background: CHART_COLORS.tooltipBg,
              border: "1px solid #1f2834",
              color: "#e6edf3",
            }}
            labelStyle={{ color: "#e6edf3" }}
            itemStyle={{ color: "#d7e3f7" }}
          />
          <Legend wrapperStyle={{ color: "#bcd1f1" }} />

          {visible?.cam41 && (
            <Bar
              dataKey="k41"
              name="41"
              stackId={stacked ? "a" : undefined}
              fill={CHART_COLORS.cam41}
              barSize={barSize}
              radius={radiusFor("k41")}
              animationDuration={450}
            />
          )}
          {visible?.chel && (
            <Bar
              dataKey="chel"
              name="Cheltenham"
              stackId={stacked ? "a" : undefined}
              fill={CHART_COLORS.chel}
              barSize={barSize}
              radius={radiusFor("chel")}
              animationDuration={450}
            />
          )}
          {visible?.pool && (
            <Bar
              dataKey="pool"
              name="Pool"
              stackId={stacked ? "a" : undefined}
              fill={CHART_COLORS.pool}
              barSize={barSize}
              radius={radiusFor("pool")}
              animationDuration={450}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
